export interface GameweekNavigationRow {
  id?: string
  gameweek_number: number
  deadline: string
  start_date?: string | null
  end_date?: string | null
}

export type GameweekNavigationStatus = 'past' | 'current' | 'future'

function sortGameweeks(rows: GameweekNavigationRow[]): GameweekNavigationRow[] {
  return [...rows].sort((left, right) => left.gameweek_number - right.gameweek_number)
}

function getTimestamp(value: string | null | undefined): number | null {
  if (!value) return null

  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export function resolveDisplayGameweek(
  rows: GameweekNavigationRow[],
  now: Date = new Date()
): GameweekNavigationRow | null {
  const sortedRows = sortGameweeks(rows)
  if (sortedRows.length === 0) return null

  const nowTime = now.getTime()
  const liveGameweek = sortedRows.find((row) => {
    const deadlineTime = getTimestamp(row.deadline)
    const endTime = getTimestamp(row.end_date)

    return deadlineTime != null && endTime != null && deadlineTime <= nowTime && nowTime <= endTime
  })

  if (liveGameweek) {
    return liveGameweek
  }

  const nextEditableGameweek = sortedRows.find((row) => {
    const deadlineTime = getTimestamp(row.deadline)
    return deadlineTime != null && deadlineTime > nowTime
  })
  return nextEditableGameweek ?? sortedRows[sortedRows.length - 1] ?? null
}

export function resolveGameweekStatus(
  row: GameweekNavigationRow,
  displayGameweekNumber: number | null
): GameweekNavigationStatus {
  if (displayGameweekNumber == null) {
    return 'future'
  }

  if (row.gameweek_number < displayGameweekNumber) {
    return 'past'
  }

  if (row.gameweek_number === displayGameweekNumber) {
    return 'current'
  }

  return 'future'
}

export function annotateGameweekStatuses(
  rows: GameweekNavigationRow[],
  now: Date = new Date()
): Array<GameweekNavigationRow & { status: GameweekNavigationStatus }> {
  const displayGameweek = resolveDisplayGameweek(rows, now)
  const displayGameweekNumber = displayGameweek?.gameweek_number ?? null

  return sortGameweeks(rows).map((row) => ({
    ...row,
    status: resolveGameweekStatus(row, displayGameweekNumber)
  }))
}

export function buildGameweekInfoFromRows(
  rows: GameweekNavigationRow[],
  season: string,
  now: Date = new Date()
): {
  currentGameweek: number
  deadline: string
  currentDeadline: string
  nextGameweekStart?: string
  season: string
  timeUntilDeadline: string
} {
  const sortedRows = sortGameweeks(rows)
  const displayGameweek = resolveDisplayGameweek(sortedRows, now)

  if (!displayGameweek) {
    return {
      currentGameweek: 0,
      season,
      deadline: now.toISOString(),
      currentDeadline: now.toISOString(),
      timeUntilDeadline: 'No active gameweek'
    }
  }

  const deadlineTime = new Date(displayGameweek.deadline)
  const timeUntil = deadlineTime.getTime() - now.getTime()

  let timeUntilDeadline = ''
  if (timeUntil > 0) {
    const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60))

    if (days > 0) {
      timeUntilDeadline = `${days}d ${hours}h`
    } else if (hours > 0) {
      timeUntilDeadline = `${hours}h ${minutes}m`
    } else {
      timeUntilDeadline = `${minutes}m`
    }
  } else {
    timeUntilDeadline = 'Deadline passed'
  }

  const followingGameweek =
    sortedRows.find((row) => row.gameweek_number > displayGameweek.gameweek_number) ?? null

  return {
    currentGameweek: displayGameweek.gameweek_number,
    season,
    deadline: displayGameweek.deadline,
    currentDeadline: displayGameweek.deadline,
    nextGameweekStart: followingGameweek?.start_date ?? undefined,
    timeUntilDeadline
  }
}

export function resolveTrackedSelectedGameweek(params: {
  autoTrackedGameweek: number | null
  currentGameweek: number | null | undefined
  selectedGameweek: number
}): number {
  const currentGameweek = params.currentGameweek ?? 0
  if (currentGameweek <= 0) {
    return params.selectedGameweek
  }

  if (params.selectedGameweek <= 0) {
    return currentGameweek
  }

  if (
    params.autoTrackedGameweek != null &&
    params.selectedGameweek === params.autoTrackedGameweek
  ) {
    return currentGameweek
  }

  return params.selectedGameweek
}

export function resolveEditableSaveGameweek(
  rows: GameweekNavigationRow[],
  selectedGameweek: number | null | undefined,
  now: Date = new Date()
): number | null {
  const selected = selectedGameweek ?? 0
  const nowTime = now.getTime()

  const selectedRow = rows.find((row) => row.gameweek_number === selected)
  const selectedDeadlineTime = getTimestamp(selectedRow?.deadline)
  if (selectedDeadlineTime != null && selectedDeadlineTime > nowTime) {
    return selected
  }

  return (
    rows.find((row) => {
      const deadlineTime = getTimestamp(row.deadline)
      return deadlineTime != null && deadlineTime > nowTime && row.gameweek_number > selected
    })?.gameweek_number ?? null
  )
}
